import capitalize from 'lodash/capitalize';
import forOwn from 'lodash/forOwn';
import mapValues from 'lodash/mapValues';
import split from 'lodash/split';
import toLower from 'lodash/toLower';
import words from 'lodash/words';
import { GRID_COLUMNS } from '@experiment-management-shared/constants/experimentGridConstants';

export const KIND_UNIQ_SYMBOL = '%%%';
export const KIND_TYPE = {
  LAST: 'LAST',
  MIN: 'MIN',
  MAX: 'MAX'
};
/**
 Normalize /metadata-v2 response to /metadata structure
 * @param {object} metadataV2
 * @returns {object} metadataV1
 */

export const convertMetadataV1 = (
  metadataV2,
  preserveKeysIfUndefined = false
) => {
  const mappedObject = {};

  forOwn(metadataV2, (value, key) => {
    const extraValuesForValue = value?.values;

    if (extraValuesForValue || preserveKeysIfUndefined) {
      if (preserveKeysIfUndefined) {
        mappedObject[key] = '';
      }

      // Special case for artifact_used - possible more than one name
      if (
        key === GRID_COLUMNS.ARTIFACT_USED &&
        Array.isArray(extraValuesForValue)
      ) {
        const artifactNames = extraValuesForValue
          .map(item => item.value)
          .join(', ');
        mappedObject[key] = artifactNames;
        return;
      }

      return extraValuesForValue.forEach(({ value, kind }) => {
        if (kind === KIND_TYPE.LAST) {
          mappedObject[key] = value;
          return;
        }

        const customKey = key + KIND_UNIQ_SYMBOL + kind;
        mappedObject[customKey] = value;
      });
    }
    mappedObject[key] = value;
  });

  return mappedObject;
};

export const normalizeColumnName = nameWithMaxMinPrefix => {
  const [keyName, kind] = split(nameWithMaxMinPrefix, KIND_UNIQ_SYMBOL);
  return { keyName, kind };
};

export const mixColumnNameWithKind = (keyName, kind) => {
  if (kind === KIND_TYPE.MIN || kind === KIND_TYPE.MAX) {
    return `${keyName}${KIND_UNIQ_SYMBOL}${kind}`;
  }
  return keyName;
};

export const formatColumnLabelWithKind = (label, kind) => {
  if (kind === KIND_TYPE.MIN || kind === KIND_TYPE.MAX) {
    return `${label} (${kind})`;
  }
  return label;
};

/*
 takes columns backend structure and for each KIND create
 a mapped object. this way we can add the min/max cols to our total columns
*/
export function getExtraCols(columns) {
  const extraOptions = [];
  columns.forEach(col =>
    col.kinds?.map(kind => {
      if (kind === KIND_TYPE.LAST) return null;

      return extraOptions.push(
        mapValues(col, (value, key) => {
          if (key === 'kinds') {
            return null;
          }

          if (key === 'id' || key === 'name') {
            return mixColumnNameWithKind(value, kind);
          }

          return value;
        })
      );
    })
  );

  return extraOptions;
}

export const getColumnName = columnName => {
  return columnName?.length ? columnName.split(KIND_UNIQ_SYMBOL)[0] : null;
};

export const getColumnType = columnName => {
  if (columnName.endsWith(`${KIND_UNIQ_SYMBOL}${KIND_TYPE.MIN}`)) {
    return KIND_TYPE.MIN;
  }

  if (columnName.endsWith(`${KIND_UNIQ_SYMBOL}${KIND_TYPE.MAX}`)) {
    return KIND_TYPE.MAX;
  }

  return KIND_TYPE.LAST;
};

export const formatSnakeCase = input =>
  input
    ? words(input, /[^_]+/g)
        .map((word, i) => (i === 0 ? capitalize(word) : toLower(word)))
        .join(' ')
    : '';
